import React from 'react';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import MicNoneIcon from '@mui/icons-material/MicNone';
import MicOffIcon from '@mui/icons-material/MicOff';
import { DictationError } from 'hooks/useDictation';

export const dictationErrorMessages: Record<DictationError, string> = {
  'permission-denied': 'Microphone access was denied. Allow it in your browser settings to dictate notes.',
  'no-microphone': 'No microphone was found. Connect one and try again.',
  network: 'The speech service is unavailable. Check your connection and try again.',
  unknown: 'Dictation stopped unexpectedly. Please try again.',
};

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

interface DictationButtonLabels {
  start: string;
  stop: string;
  unsupported: string;
}

const defaultLabels: DictationButtonLabels = {
  start: 'Start dictation',
  stop: 'Stop dictation',
  unsupported: "Voice dictation isn't supported in this browser. Try Chrome, Edge or Safari.",
};

interface DictationButtonProps extends Omit<IconButtonProps, 'onClick' | 'children'> {
  isSupported: boolean;
  isListening: boolean;
  onStart: () => void;
  onStop: () => void;
  labels?: Partial<DictationButtonLabels>;
}

export const DictationButton = ({
  isSupported,
  isListening,
  onStart,
  onStop,
  labels: labelOverrides,
  disabled,
  ...buttonProps
}: DictationButtonProps) => {
  const labels = { ...defaultLabels, ...labelOverrides };

  if (!isSupported) {
    return (
      <Tooltip title={labels.unsupported}>
        {/* Disabled buttons don't fire mouse events, so the tooltip needs a wrapper */}
        <span>
          <IconButton {...buttonProps} disabled aria-label={labels.unsupported}>
            <MicOffIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const label = isListening ? labels.stop : labels.start;

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          {...buttonProps}
          disabled={disabled}
          aria-label={label}
          aria-pressed={isListening}
          color={isListening ? 'error' : 'default'}
          onClick={isListening ? onStop : onStart}
        >
          {isListening ? <MicIcon sx={{ animation: `${pulse} 1.5s ease-in-out infinite` }} /> : <MicNoneIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
